/**
 * "ABCDior Settings". Info message, screensavers, backgrounds and, on the MTM
 * platform, the app notifications. Admin only.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES, type Platform } from '@/shared/constants';
import { appConfigQueryOptions } from '@/shared/config/app-config';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { snackbar } from '@/shared/ui/snackbar';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import plusIcon from '@/assets/images/plus.svg';
import {
  backgroundsQueryOptions,
  createNotification,
  hasError,
  infoMessageQueryOptions,
  notificationsQueryOptions,
  screenSaversQueryOptions,
  type Asset,
} from './api';
import {
  backgroundPictureUrl,
  extraRows,
  formatDate,
  resolveStores,
  storesLabel,
} from './logic';
import { InfoMessageForm } from './InfoMessageForm';
import { ScreenSaverDialog } from './ScreenSaverDialog';
import { BackgroundDialog } from './BackgroundDialog';

type DialogState = {
  kind: 'screensaver' | 'background';
  item: Asset | null;
} | null;

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export function InfoPage({
  platform = PLATFORM.ABCDIOR,
}: Readonly<{
  platform?: Platform;
}>) {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const isDms = platform === PLATFORM.DMS;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const info = useQuery({
    ...infoMessageQueryOptions(platform),
    enabled: isAdmin,
  });

  // --- notifications (MTM) ---
  const [notification, setNotification] = useState('');
  const [confirmingNotification, setConfirmingNotification] = useState(false);
  const notifications = useQuery({
    ...notificationsQueryOptions(PLATFORM.DMS),
    enabled: isAdmin && isDms,
  });
  const sendNotification = useMutation({
    mutationFn: () => createNotification({ platform, message: notification }),
    onSuccess: (data) => {
      if (hasError(data)) {
        snackbar(
          `An error occurred while creating the notification: ${data.error}`,
        );
        return;
      }
      snackbar('Successfully created the notification');
      setNotification('');
      setTimeout(
        () =>
          void queryClient.invalidateQueries({
            queryKey: notificationsQueryOptions(PLATFORM.DMS).queryKey,
          }),
        250,
      );
    },
    onError: (error) => {
      console.error(error);
      snackbar(
        `An error occurred while creating the notification: ${errorText(error)}`,
      );
    },
  });

  // --- screensavers & backgrounds ---
  const { data: config } = useQuery(appConfigQueryOptions());
  const stores = useMemo(() => config?.stores ?? [], [config?.stores]);
  const screenSavers = useQuery({
    ...screenSaversQueryOptions(),
    enabled: isAdmin,
  });
  const backgrounds = useQuery({
    ...backgroundsQueryOptions(),
    enabled: isAdmin,
  });
  const [dialog, setDialog] = useState<DialogState>(null);

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    const kind = dialog?.kind;
    setDialog(null);
    if (result !== 'Confirm') return;
    snackbar('Successfully saved');
    void queryClient.invalidateQueries({
      queryKey:
        kind === 'screensaver'
          ? screenSaversQueryOptions().queryKey
          : backgroundsQueryOptions().queryKey,
    });
  };

  if (!isAdmin) return null;

  const assetRow = (item: Asset, media: ReactNode, mediaTestId: string) => (
    <tr key={item.id}>
      <td
        style={{ width: '20%' }}
        title={item.name ?? ''}
        data-testid="settings-row-name"
      >
        {item.name}
      </td>
      <td
        style={{ width: '20%' }}
        title={formatDate(item.createdAt)}
        data-testid="settings-row-creation-date"
      >
        {formatDate(item.createdAt)}
      </td>
      <td
        style={{ width: '20%' }}
        title={item.active ? 'YES' : 'NO'}
        data-testid="settings-row-is-active"
      >
        {item.active ? 'YES' : 'NO'}
      </td>
      <td
        style={{ width: '20%' }}
        title={typeof media === 'string' ? media : ''}
        data-testid={mediaTestId}
      >
        {media}
      </td>
      <td style={{ width: '20%' }} data-testid="settings-row-assigned-stores">
        {storesLabel(resolveStores(item.assignedStores, stores))}
      </td>
      <td style={{ width: '10%' }} className="edit-action">
        <button
          type="button"
          className="icon-button pointer-cursor action-icon"
          aria-label={`Edit ${item.name ?? ''}`}
          data-testid="settings-row-edit-button"
          onClick={() =>
            setDialog({
              kind: item.type === 'background' ? 'background' : 'screensaver',
              item,
            })
          }
        >
          <FontAwesomeIcon icon={faPencilAlt} />
        </button>
      </td>
    </tr>
  );

  const emptyRows = (count: number, columns: number[]) =>
    Array.from({ length: count }, (_, index) => (
      <tr key={`empty-${index}`} className="empty-row">
        {columns.map((width, cell) => (
          <td key={cell} style={{ width: `${width}%` }} />
        ))}
      </tr>
    ));

  return (
    <div className="container summary">
      <TopMenu platform={platform} />
      <div className="my-12 flex flex-col">
        <h1 className="text-center">ABCDior Settings</h1>

        <h2 className="mt-6 text-center">Info Message</h2>
        {(info.isSuccess || info.isError) && (
          <InfoMessageForm
            platform={platform}
            initial={info.isSuccess ? info.data : null}
          />
        )}

        <h2 className="mt-6 text-center">Screensaver</h2>
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid="header-add-screensaver"
          onClick={() => setDialog({ kind: 'screensaver', item: null })}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a screensaver</span>
        </button>
        <div className="table-container">
          <div
            className="items-container"
            style={{ maxHeight: 420, overflow: 'auto' }}
            role="region"
            aria-label="Screensavers"
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Name</th>
                  <th style={{ width: '20%' }}>Creation Date</th>
                  <th style={{ width: '20%' }}>Is Active</th>
                  <th style={{ width: '20%' }}>URL</th>
                  <th style={{ width: '20%' }}>Assigned Stores</th>
                  <th style={{ width: '10%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(screenSavers.data ?? []).map((item) =>
                  assetRow(item, item.url ?? '', 'settings-row-url'),
                )}
                {emptyRows(
                  extraRows(screenSavers.data?.length ?? 0),
                  [20, 20, 20, 20, 20, 10],
                )}
              </tbody>
            </table>
          </div>
        </div>

        <h2 className="mt-6 text-center">Background</h2>
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid="header-add-background"
          onClick={() => setDialog({ kind: 'background', item: null })}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a background</span>
        </button>
        <div className="table-container">
          <div
            className="items-container"
            style={{ maxHeight: 420, overflow: 'auto' }}
            role="region"
            aria-label="Backgrounds"
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Name</th>
                  <th style={{ width: '20%' }}>Creation Date</th>
                  <th style={{ width: '20%' }}>Is Active</th>
                  <th style={{ width: '20%' }}>Image</th>
                  <th style={{ width: '20%' }}>Assigned Stores</th>
                  <th style={{ width: '10%' }} className="edit-header">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(backgrounds.data ?? []).map((item) =>
                  assetRow(
                    item,
                    <img
                      className="background-preview"
                      src={backgroundPictureUrl(item) ?? undefined}
                      alt={item.name ?? ''}
                    />,
                    'settings-row-image',
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>

        {isDms && (
          <>
            <div className="mt-6 border-t border-neutral-300 pt-6">
              <div className="w-8/12">
                <h3>Notification on app</h3>
                <textarea
                  className="field"
                  name="notification"
                  placeholder="Notification"
                  aria-label="Notification"
                  data-testid="settings-notification-input"
                  style={{ height: 130 }}
                  value={notification}
                  onChange={(event) => setNotification(event.target.value)}
                />
                <button
                  className="button button-form"
                  type="button"
                  disabled={!notification || sendNotification.isPending}
                  onClick={() => setConfirmingNotification(true)}
                  data-testid="settings-notification-send-button"
                >
                  Send
                </button>
              </div>
            </div>
            <div className="mt-6">
              <h3>Message history</h3>
              <div className="table-container">
                <div
                  className="items-container"
                  role="region"
                  aria-label="Message history"
                >
                  <table className="table items">
                    <thead>
                      <tr>
                        <th style={{ width: '50%' }}>Message</th>
                        <th style={{ width: '30%' }}>Type</th>
                        <th style={{ width: '20%' }}>Sending date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(notifications.data ?? []).map((item, index) => (
                        <tr
                          key={item.id ?? item._id ?? index}
                          className="store-row"
                        >
                          <td
                            className="text-ellipsis"
                            style={{ width: '50%' }}
                            title={item.message ?? ''}
                            data-testid="settings-notification-row-message"
                          >
                            {item.message}
                          </td>
                          <td
                            style={{ width: '30%' }}
                            title={item.type ?? ''}
                            data-testid="settings-notification-row-type"
                          >
                            Notification on app
                          </td>
                          <td
                            style={{ width: '20%' }}
                            title={formatDate(item.createdAt)}
                            data-testid="settings-notification-row-sending-date"
                          >
                            {formatDate(item.createdAt)}
                          </td>
                        </tr>
                      ))}
                      {Array.from(
                        { length: extraRows(notifications.data?.length ?? 0) },
                        (_, index) => (
                          <tr
                            key={`empty-${index}`}
                            className="store-row empty-row"
                          >
                            <td style={{ width: '50%' }} />
                            <td style={{ width: '30%' }} />
                            <td style={{ width: '20%' }} />
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {confirmingNotification && (
              <ConfirmDialog
                type="question"
                text={`Please confirm the notification: "${notification}"`}
                onConfirm={() => {
                  setConfirmingNotification(false);
                  sendNotification.mutate();
                }}
                onCancel={() => setConfirmingNotification(false)}
              />
            )}
          </>
        )}
      </div>

      {dialog?.kind === 'screensaver' && (
        <ScreenSaverDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          stores={stores}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'background' && (
        <BackgroundDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          stores={stores}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
